/**
 * Notifications Pop-Out Panel Content
 *
 * Full notification list panel for the desktop Activity Rail.
 * Uses the existing useNotifications hook and notifications API.
 *
 * Features:
 * - Scrollable notification list with read/unread distinction
 * - Mark individual or all as read
 * - Dismiss notifications
 * - Click-through navigation to action URL
 * - Load more pagination
 * - Loading, error, and empty states
 */
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheckDouble,
  faTimes,
  faChartLine,
  faExclamationTriangle,
  faInfoCircle,
  faSpinner,
  faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import { useNotifications } from '../../../hooks/useNotifications';
import type { Notification } from '../../../services/api/notifications';
import { getNotificationNavigationTarget } from '../../../utils/notificationNavigation';
import { getNotificationDisplayMessage } from '../../../utils/notificationText';

interface NotificationsPopOutProps {
  onClose: () => void;
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

const getNotificationIcon = (type: Notification['type']) => {
  switch (type) {
    case 'optimization_complete':
    case 'wfa_complete':
    case 'backtest_complete':
      return { icon: faChartLine, colorClass: 'text-green-500' };
    case 'optimization_failed':
    case 'wfa_failed':
    case 'backtest_failed':
      return { icon: faExclamationTriangle, colorClass: 'text-red-500' };
    case 'system':
      return { icon: faInfoCircle, colorClass: 'text-blue-500' };
    default:
      return { icon: faInfoCircle, colorClass: 'text-gray-400' };
  }
};

export const NotificationsPopOut: React.FC<NotificationsPopOutProps> = ({ onClose }) => {
  const navigate = useNavigate();

  const {
    notifications,
    unreadCount,
    loading,
    error,
    pagination,
    markAsRead,
    markAllAsRead,
    dismiss,
    refresh,
    fetchNotifications,
  } = useNotifications({
    autoFetch: true,
    pollInterval: 30000,
    limit: 20,
  });

  const handleNotificationClick = useCallback(async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    const target = getNotificationNavigationTarget(notification.actionUrl);
    if (target) {
      onClose();
      navigate(target);
    }
  }, [markAsRead, navigate, onClose]);

  const handleDismiss = useCallback(async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await dismiss(id);
  }, [dismiss]);

  const handleLoadMore = useCallback(() => {
    if (pagination && pagination.page < pagination.pages) {
      fetchNotifications(pagination.page + 1);
    }
  }, [pagination, fetchNotifications]);

  const hasMore = pagination ? pagination.page < pagination.pages : false;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
        </span>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllAsRead()}
            className="flex items-center gap-1.5 text-xs text-[#9d4edd] hover:text-[#8b3ec7] transition-colors"
            title="Mark all as read"
          >
            <FontAwesomeIcon icon={faCheckDouble} className="text-xs" />
            Mark all read
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && notifications.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl text-amber-500 mb-3 opacity-75" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Unable to load notifications</p>
            <button
              onClick={() => refresh()}
              className="mt-3 flex items-center gap-1.5 text-xs text-[#9d4edd] hover:text-[#8b3ec7] transition-colors"
            >
              <FontAwesomeIcon icon={faRotateRight} className="text-xs" />
              Try again
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <FontAwesomeIcon icon={faBell} className="text-4xl text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Completed jobs will appear here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {notifications.map((notification) => {
              const { icon, colorClass } = getNotificationIcon(notification.type);
              const target = getNotificationNavigationTarget(notification.actionUrl);
              const isClickable = Boolean(target);

              return (
                <div
                  key={notification.id}
                  onClick={isClickable ? () => handleNotificationClick(notification) : undefined}
                  className={`
                                        px-4 py-3 transition-colors
                                        ${isClickable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5' : ''}
                                        ${!notification.read ? 'bg-[#9d4edd]/5 dark:bg-[#9d4edd]/10' : ''}
                                    `}
                >
                  <div className="flex items-start gap-3">
                    {/* Type icon */}
                    <div className={`mt-0.5 flex-shrink-0 ${colorClass}`}>
                      <FontAwesomeIcon icon={icon} className="text-sm" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium leading-snug truncate ${!notification.read
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-600 dark:text-gray-300'
                          }`}>
                          {notification.title}
                        </p>
                        <button
                          onClick={(e) => handleDismiss(e, notification.id)}
                          className="flex-shrink-0 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 transition-colors p-0.5"
                          title="Dismiss"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-xs" />
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
                        {getNotificationDisplayMessage(notification)}
                      </p>

                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {formatTimeAgo(notification.createdAt)}
                        </span>
                        {!notification.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#9d4edd] flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Load more */}
            {hasMore && (
              <div className="px-4 py-3">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="w-full text-xs text-[#9d4edd] hover:text-[#8b3ec7] disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5 py-1"
                >
                  {loading
                    ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Loading...</>
                    : 'Load more'
                  }
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPopOut;
